// External packages
import * as React from 'react';
import * as z from 'zod';

// Components
import { Option } from 'components/Select';
import * as Modal from 'components/Modal';
import { Form } from 'components/Form';
import { Label } from 'components/Forms';
import { SelectMultiple } from 'components/SelectMultiple';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import { TiptapEditorField } from 'components/TiptapEditor';
import { Button } from 'components/Button';

// Modules
import {
  AddValue,
  Language,
  TranslationsCleaner,
} from 'modules/exhibits/ExhibitsModal';

// Hooks
import { useAddImageTranslations } from 'api/media';

// Types
import type { Image } from '../../../../server/src/types/media.types';

// Data
import languagesData from 'data/languages.json';

// Utils
import { getBestImageUrl } from 'utils/getImageUrl';

const imageTranslationsFormSchema = z.object({
  translations: z.array(
    z.object({
      languageCode: z.string(),
      description: z.string({ message: 'Dodajte opis' }),
    })
  ),
});

export const ImageGalleryItem: React.FC<{
  item: Image;
  modalTrigger: React.ReactNode;
}> = ({ item, modalTrigger }) => {
  const [imageModalOpen, setImageModalOpen] = React.useState(false);

  const { mutate: addTranslations, isPending: isAddingTranslations } =
    useAddImageTranslations();

  const languages: Language[] = languagesData;

  const [selectedLanguages, setSelectedLanguages] = React.useState<Language[]>(
    () => {
      if (item && item.translations) {
        const langs = item.translations
          .map((t) => languages.find((l) => l.langCode === t.languageCode))
          .filter(Boolean) as Language[];

        return Array.from(new Set(langs.map((l) => l.langCode))).map(
          (code) => languages.find((l) => l.langCode === code)!
        );
      }

      return [languages[0]];
    }
  );

  const handleLanguageChange = (keys: unknown) => {
    let arr: string[] = Array.isArray(keys)
      ? keys.map(String)
      : keys instanceof Set
        ? Array.from(keys).map(String)
        : keys
          ? [String(keys)]
          : [];
    arr = Array.from(new Set(arr));

    if (arr.length === 0) {
      return;
    }

    const langs = arr
      .map((code) => languages.find((l) => l.langEnglishName === code))
      .filter(Boolean) as Language[];

    setSelectedLanguages(langs);
  };

  React.useEffect(() => {
    if (
      !item.translations?.some((t) =>
        selectedLanguages.some((l) => l.langCode === t.languageCode)
      )
    ) {
      return setSelectedLanguages([languages[0]]);
    }
  }, [item, languages]);

  const onSubmit = (data: z.infer<typeof imageTranslationsFormSchema>) => {
    addTranslations(
      {
        imageId: item.id,
        translations: { translations: data.translations },
      },
      {
        onSuccess: () => setImageModalOpen(false),
      }
    );
  };

  return (
    <Modal.TriggerRoot isOpen={imageModalOpen} onOpenChange={setImageModalOpen}>
      {modalTrigger}
      <Modal.Overlay isDismissable>
        <Modal.Root>
          <Modal.Dialog>
            <Modal.Close />
            <h3 className="mb-8 text-2xl text-gray-600">Uredi prijevode</h3>
            <img
              className="max-h-[calc(100vh-9.5rem)] mx-auto rounded-lg"
              src={getBestImageUrl(item, 'large')}
              alt={item.altText || ''}
            />
            <Form
              schema={imageTranslationsFormSchema}
              onSubmit={onSubmit}
              defaultValues={{
                translations: selectedLanguages.map((lang) => ({
                  languageCode: lang.langCode,
                  description:
                    item.translations?.find(
                      (t) => t.languageCode === lang.langCode
                    )?.description || '',
                })),
              }}
            >
              <TranslationsCleaner selectedLanguages={selectedLanguages} />
              <div className="mb-4">
                <Label>Odaberi jezike</Label>
                <SelectMultiple
                  selectedKeys={selectedLanguages.map((l) => l.langEnglishName)}
                  onSelectionChange={handleLanguageChange}
                >
                  {languages &&
                    languages.map((lang) => (
                      <Option key={lang.langCode} id={lang.langEnglishName}>
                        {lang.langNativeName} ({lang.langEnglishName})
                      </Option>
                    ))}
                </SelectMultiple>
              </div>
              <Tabs className="mb-4">
                <TabList className="!justify-start">
                  {selectedLanguages.map((lang) => (
                    <Tab key={lang.langCode} id={lang.langEnglishName}>
                      {lang.langEnglishName}
                    </Tab>
                  ))}
                </TabList>
                {selectedLanguages.map((lang) => {
                  const index = selectedLanguages.findIndex(
                    (l) => l.langCode === lang.langCode
                  );

                  return (
                    <TabPanel
                      key={lang.langCode}
                      id={lang.langEnglishName}
                      className="px-4 py-6"
                    >
                      <AddValue index={index} langCode={lang.langCode} />
                      <TiptapEditorField
                        isRequired
                        name={`translations.${index}.description`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                    </TabPanel>
                  );
                })}
              </Tabs>
              <Button
                type="submit"
                className="w-full mt-12"
                size="lg"
                isPending={isAddingTranslations}
              >
                Uredi prijevode
              </Button>
            </Form>
          </Modal.Dialog>
        </Modal.Root>
      </Modal.Overlay>
    </Modal.TriggerRoot>
  );
};
